/** @format */

import React from "react";
import "./ResourcePage.scss";
import {
  useGetResourceQuery,
  useGetResourceReferenceQuery,
} from "../../../app/services/resourcesApi";
import { useParams } from "react-router-dom";
import { useLocale } from "../../../i18n/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import { ReactComponent as InfoCircle } from "bootstrap-icons/icons/info-circle.svg";
import ResourcePageUI from "./ResourcePageUI";
import { useGetSubjectsQuery } from "../../../app/services/subjectApi";
import { QualificationId } from "../../../types";
import BreadCrumb from "../../breadcrumb/BreadCrumb";
import { subjectNameToId } from "../../../utils/functions";
import ErrorPage from "../../errorPage/ErrorPage";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { useGetUserQuery } from "../../../app/services/userApi";

const ResourcePage: React.FC = () => {
  const { data: subjectsResponse } = useGetSubjectsQuery();
  const { resourceId, qualification, type, subject } = useParams();
  const locale = useLocale();
  const { t: _ } = useTranslation();

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );

  const {
    data: resource,
    error: resourceError,
    isFetching,
  } = useGetResourceReferenceQuery(
    resourceId && type && subject && qualification && subjectsResponse
      ? {
          code: resourceId,
          locale,
          type: type,
          subjectId: subjectNameToId(subject, subjectsResponse.subjects),
          qualification: qualification as QualificationId,
        }
      : skipToken,
  );

  const { isFetching: isBlResourceIsLoading, error: blResourceError } =
    useGetResourceQuery(
      resourceId && resource && resource.Type === "BL" ? resourceId : skipToken,
    );

  if (resource !== undefined && !isFetching && !isBlResourceIsLoading) {
    return (
      <>
        <BreadCrumb resource={resource} />
        {/** user can edit and the branding is single language */}
        {user?.role === "wjec" &&
          (resource.Branding === "wjec" ||
            resource.Branding === "eduqas" ||
            resource.Branding === "cbac") && (
            <div
              className="user-resource-info banner-info py-3 my-3 px-2 px-sm-3 "
              style={{ minHeight: "calc(2rem)" }}
            >
              <div className="container-fluid d-flex flex-row align-items-center gap-2 ps-0">
                <InfoCircle className="flex-shrink-0" />
                <p className="m-0">
                  <small>
                    {_(
                      "This is a single language resource. All change on this page will only update the resource reference.",
                    )}
                  </small>
                </p>
              </div>
            </div>
          )}
        <ResourcePageUI resourceRef={resource} preview={false} />
      </>
    );
  } else if (resourceError !== undefined) {
    return (
      <ErrorPage
        statusCode={
          "status" in resourceError && typeof resourceError.status === "number"
            ? resourceError.status
            : undefined
        }
        hiddenMessage={
          "data" in resourceError && typeof resourceError.data === "string"
            ? resourceError.data
            : undefined
        }
        actions={["report", "subject"]}
      />
    );
  } else if (blResourceError !== undefined) {
    return (
      <ErrorPage
        statusCode={
          "status" in blResourceError &&
          typeof blResourceError.status === "number"
            ? blResourceError.status
            : undefined
        }
        hiddenMessage={
          "data" in blResourceError && typeof blResourceError.data === "string"
            ? blResourceError.data
            : undefined
        }
        actions={["report", "subject"]}
      />
    );
  } else {
    return (
      <div className="position-absolute top-50 start-50 translate-middle">
        <Spinner />
      </div>
    );
  }
};

export default ResourcePage;
